import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import DropzoneS3Uploader from 'react-dropzone-s3-uploader';
import {
  CloudUpload as CloudUploadIcon,
  FilePresent as FilePresentIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

import { scrubFileName } from '../../../../../../../helper';
import { THomeworkFile } from '../../../../../../../store/V2/HomeworksSlice';
import BasicLoader from '../../../../../../../components/Loaders/BasicLoader';

const dropzoneStyles = {
  height: '150px',
  borderRadius: '8px',
  border: '1px dashed #CBD5E1',
  color: '#94A3B8',
  background: '#F1F5F9',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
};

const notDropzoneProps = [
  'onFinish',
  's3Url',
  'filename',
  'host',
  'upload',
  'isImage',
  'notDropzoneProps',
  'onDrop',
];

type TState = {
  isUploading: boolean;
  uploadPercentage: number;
  uploadingFileCount: number;
  files: THomeworkFile[];
  uploadItemIndex: number;
};

const initialState: TState = {
  isUploading: false,
  uploadPercentage: 0,
  uploadingFileCount: 0,
  files: [],
  uploadItemIndex: 0,
};

interface UploadHomeworkFilesProps {
  onFinish: (files: THomeworkFile[]) => void;
  prevFiles?: THomeworkFile[];
}

const UploadHomeworkFiles: React.FC<UploadHomeworkFilesProps> = ({
  onFinish,
  prevFiles,
}) => {
  // State
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (prevFiles && prevFiles.length) {
      setState((prevState) => ({
        ...prevState,
        files: prevFiles,
      }));
    }
  }, []);

  const handleDropUpload = (files) => {
    setState((prevState) => ({
      ...prevState,
      isUploading: true,
      uploadingFileCount: prevState.uploadingFileCount + files.length,
    }));
  };

  const onUploadProgress = (event) => {
    setState((prevState) => ({
      ...prevState,
      uploadPercentage: event,
    }));
  };

  const handleFinishedUpload = async (file) => {
    const newFile = {
      name: file.objectName,
      url: `${process.env.REACT_APP_TUTOPIYA_S3_HOMEWORK_BUCKET_URL}/${file.objectName}`,
    };
    setState((prevState) => {
      const newState: TState = {
        ...prevState,
        files: [...prevState.files, newFile],
        uploadingFileCount:
          prevState.uploadingFileCount > 1
            ? prevState.uploadingFileCount - 1
            : 0,
        isUploading: prevState.uploadingFileCount > 1,
        uploadPercentage: 0,
      };

      onFinish([...prevState.files, newFile]);

      return newState;
    });
  };

  const handleRemove = (file) => {
    const newFiles = state.files.filter((newFile) => file.url !== newFile.url);
    setState((prevState) => ({
      ...prevState,
      files: newFiles,
    }));
    onFinish(newFiles);
  };

  return (
    <div>
      <DropzoneS3Uploader
        s3Url={process.env.REACT_APP_TUTOPIYA_S3_HOMEWORK_BUCKET_URL}
        maxSize={1024 * 1024 * 100}
        onFinish={handleFinishedUpload}
        onProgress={onUploadProgress}
        onDrop={handleDropUpload}
        style={dropzoneStyles}
        notDropzoneProps={notDropzoneProps}
        upload={{
          server: `${process.env.REACT_APP_TUTOPIYA_BASE_URL_V2}/homeworks/file`,
          signingUrlQueryParams: {},
          scrubFilename: scrubFileName,
        }}
      >
        {state.isUploading ? (
          <>
            <BasicLoader />
            <Typography
              fontSize="12px"
              marginTop="20px"
            >{`Uploading in Progress - ${state.uploadPercentage}% (${
              state.uploadingFileCount
            } ${
              state.uploadingFileCount > 1 ? 'files' : 'file'
            } Remaining)`}</Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon fontSize="large" />
            <Typography fontSize="12px">Upload file</Typography>
          </>
        )}
      </DropzoneS3Uploader>
      {state.files.length > 0 && (
        <Grid container marginTop="20px" rowGap="10px">
          {state.files.map((file, index) => (
            <Grid item key={index} xs={12}>
              <Stack
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: '#F1F5F9',
                  padding: '10px',
                  borderRadius: '5px',
                }}
              >
                <Box display="flex" alignItems="center">
                  <FilePresentIcon fontSize="small" />
                  <Typography fontSize="12px" marginLeft="10px">
                    {file.name}
                  </Typography>
                </Box>
                <IconButton onClick={() => handleRemove(file)} size="small">
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default UploadHomeworkFiles;
